import * as React from "react";
import { Loader2 } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

// Drop-in for the previous CSS-border spinner. Switched to the lucide
// ``Loader2`` SVG so the icon renders with intrinsic width/height even
// inside flex children — the old wrapper span had no display: block, so
// in some buttons it collapsed to a single-pixel vertical line. SVG
// nodes are inline-replaced and never get squished by the parent.
const spinnerVariants = cva("animate-spin shrink-0 text-current", {
  variants: {
    size: {
      sm: "h-4 w-4",
      md: "h-5 w-5",
      lg: "h-7 w-7",
      xl: "h-10 w-10",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

export interface SpinnerProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children">,
    VariantProps<typeof spinnerVariants> {
  /** Accessible label — defaults to "Загрузка…" */
  label?: string;
}

function Spinner({ className, size, label = "Загрузка…", ...props }: SpinnerProps) {
  return (
    <span
      role="status"
      aria-label={label}
      className="inline-flex items-center justify-center"
      {...props}
    >
      <Loader2 className={cn(spinnerVariants({ size }), className)} aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </span>
  );
}

export { Spinner, spinnerVariants };
